
import React, { useEffect, useState } from 'react';
import { getArticles, getWriters, slugify, getMediaUrl } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import ProfileCarousel from '../components/ProfileCarousel';
import { useNavigate, Link } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';

interface Article {
  id: string;
  title: string;
  slug: string;
  content: string | any[];
  category: string;
  coverImage?: { url: string };
  publishedDate: string | null;
  writer?: any;
  excerpt?: string;
}

const KadininKalemi: React.FC = () => {
  const { t } = useTranslation();
  const { locale } = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [writers, setWriters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 14;

  useEffect(() => {
    const fetchArticlesAndWriters = async () => {
      if (page === 1) setLoading(true);
      try {
        const [articlesRes, writersData] = await Promise.all([
          getArticles(undefined, locale, page, pageSize, 'KadininKalemi'),
          page === 1 ? getWriters(locale) : Promise.resolve(null)
        ]);
        
        if (writersData) {
          const mappedWriters = writersData.map((writer: any) => ({
            ...writer,
            image: writer.avatar // Map avatar to image for ProfileCarousel
          }));
          setWriters(mappedWriters);
        }

        const { data, meta } = articlesRes;
        const filtered = data
          .map((item: any) => ({
            id: item.documentId || item.id,
            title: item.title,
            slug: item.slug || slugify(item.title) || item.documentId || item.id,
            content: item.content,
            category: item.category,
            coverImage: item.coverImage,
            publishedDate: formatDate(item.publishedDate, locale),
            writer: item.writer,
            excerpt: item.content?.[0]?.children?.[0]?.text || ''
          }));
        
        if (page === 1) {
          setArticles(filtered);
        } else {
          setArticles(prev => [...prev, ...filtered]);
        }

        if (meta && meta.pagination) {
          setHasMore(page < meta.pagination.pageCount);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Failed to load content", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticlesAndWriters();
  }, [locale, page]);

  const featuredArticles = articles.slice(0, 2);
  const others = articles.slice(2);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-neutral-bg">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-brand-purple-900"></div>
      </div>
    );
  }

  // Removed early return to keep page header and footer intact



  return (
    <div className="bg-brand-neutral-bg min-h-screen py-20 animate-in fade-in duration-1000">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page Header */}
        <div className="text-center mb-10 space-y-4">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-brand-purple-900 italic tracking-tight">{t('kalem_page.title')}</h1>
          <p className="text-xl text-gray-500 font-light max-w-2xl mx-auto">
            {t('kalem_page.desc')}
          </p>
          <div className="w-24 h-1 bg-brand-yellow-400 mx-auto mt-8"></div>
        </div>
      </div>

      {/* Writers / Profiles Component */}
      {writers.length > 0 && (
        <div className="mb-8">
          <ProfileCarousel profiles={writers} className="bg-transparent py-4" />
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Featured Stories */}
        {featuredArticles.length > 0 ? (
          <section className="mb-20 max-w-full mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {featuredArticles.map((featuredItem) => (
                <Link to={`/${locale}/haberler/${featuredItem.slug}`} key={featuredItem.id} className="bg-white rounded-[2rem] overflow-hidden shadow-2xl shadow-brand-purple-900/5 flex flex-col md:flex-row items-stretch border border-brand-purple-50 min-h-[300px]">
                  <div className="md:w-2/5 h-[250px] md:h-auto overflow-hidden">
                    <img
                      src={featuredItem.coverImage?.url ? getMediaUrl(featuredItem.coverImage.url) : 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=1200'}
                      alt={featuredItem.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                    />
                  </div>
                  <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center h-full">
                    <div className="flex items-center space-x-2 text-brand-green-500 font-bold text-xs uppercase tracking-widest mb-3">
                      {featuredItem.publishedDate && <span>{featuredItem.publishedDate}</span>}
                    </div>

                    <div>
                      <h2 className="text-xl md:text-2xl font-serif font-bold text-brand-purple-900 leading-tight mb-2 hover:text-brand-purple-600 cursor-pointer transition-colors">
                        {featuredItem.title}
                      </h2>
                      {featuredItem.excerpt && (
                        <p className="text-sm text-gray-600 line-clamp-2 mt-2 mb-4">{featuredItem.excerpt}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-8 border-t border-gray-100 pt-4 mt-4">
                      {featuredItem.writer && (
                        <div className="flex items-center gap-6">
                          {featuredItem.writer.avatar?.url ? (
                            <img src={getMediaUrl(featuredItem.writer.avatar.url)} alt={featuredItem.writer.name} className="w-14 h-14 rounded-full object-cover" />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-brand-purple-100 flex items-center justify-center text-brand-purple-900 font-bold text-xs">
                              {featuredItem.writer.name.substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <h4 className="font-bold text-xs text-brand-purple-900">{featuredItem.writer.name}</h4>
                        </div>
                      )}
                      <div className="ms-auto w-8 h-8 rounded-full bg-brand-purple-500 text-white flex items-center justify-center hover:bg-brand-purple-600 transition-all hover:scale-110 shadow-lg">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ) : (
          <div className="py-20 mb-20 text-center">
            <h2 className="text-3xl text-gray-400 font-serif">{t('kalem_page.empty')}</h2>
            <p className="text-gray-500 mt-4">{t('kalem_page.empty_sub')}</p>
          </div>
        )}

        {/* Stories Grid */}
        {others.length > 0 && (
          <section className="space-y-16">
          <div className="flex items-center justify-between">
            <h3 className="text-3xl font-serif font-bold text-brand-purple-900">{t('kalem_page.latest')}</h3>
            <div className="h-px flex-1 bg-brand-purple-100 mx-8 hidden md:block"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {others.map((story) => (
              <Link to={`/${locale}/haberler/${story.slug}`} key={story.id} className="group cursor-pointer bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl border border-gray-50 transition-all duration-500 hover:-translate-y-1 flex flex-col justify-between h-full">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={story.coverImage?.url ? getMediaUrl(story.coverImage.url) : 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=800'}
                    alt={story.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-purple-900/60 via-brand-purple-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>

                <div className="space-y-4 p-6 flex flex-col flex-1 justify-between">
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center space-x-3 text-xs text-gray-400 font-medium mb-2">
                      {story.publishedDate && <span>{story.publishedDate}</span>}
                      {story.writer && (
                        <>
                          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                          <span className="text-brand-purple-500">{story.writer.name}</span>
                        </>
                      )}
                    </div>

                    <h4 className="text-2xl font-serif font-bold text-brand-purple-900 group-hover:text-brand-purple-600 transition-colors leading-tight line-clamp-3">
                      {story.title}
                    </h4>
                  </div>

                  <button className="text-brand-green-500 font-bold text-sm flex items-center pt-2 group-hover:translate-x-1 transition-transform mt-auto">
                    {t('kalem_page.read')}
                    <svg className="w-4 h-4 ms-2 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </Link>
            ))}
          </div>
          </section>
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-16 flex justify-center">
            <button
              onClick={() => setPage(p => p + 1)}
              className="bg-brand-purple-900 border border-brand-purple-900 text-white font-bold px-10 py-4 rounded-2xl shadow-lg hover:bg-brand-purple-700 transition-all active:scale-95 disabled:opacity-50"
            >
              {t('resources.load_more')}
            </button>
          </div>
        )}

        {/* Call to Authoring (Yazınızı Gönderin)
        <section className="mt-40 mb-20">
          <div className="bg-brand-purple-900 text-white rounded-[3rem] p-12 lg:p-20 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-yellow-400 rounded-full blur-[100px] opacity-10 -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-green-500 rounded-full blur-[100px] opacity-10 translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10 max-w-2xl mx-auto space-y-8">
              <h2 className="text-4xl md:text-5xl font-serif font-bold leading-tight">{t('kalem_page.cta_title')}</h2>
              <p className="text-lg text-brand-purple-100/80 font-light leading-relaxed">
                {t('kalem_page.cta_desc')}
              </p>
              <button onClick={() => navigate(`/${locale}/iletisim`)} className="bg-brand-yellow-400 text-brand-purple-900 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white transition-all shadow-xl hover:-translate-y-1 active:scale-95">
                {t('kalem_page.cta_btn')}
              </button>
            </div>
          </div>
        </section>
        */}
      </div>
    </div>
  );
};

export default KadininKalemi;
